import { useEffect, useState } from 'react';
import { Wallet, Loader2, CheckCircle2, AlertTriangle, Clock, Info } from 'lucide-react';
import api from '../../lib/api';

const STATUS_BADGE = {
  paid: 'bg-emerald-500/10 text-emerald-600',
  pending: 'bg-amber-500/10 text-amber-600',
  overdue: 'bg-red-500/10 text-red-600',
};

const STATUS_LABELS = { paid: 'Pagada', pending: 'Pendiente', overdue: 'En mora' };

export default function MemberDues() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/me/invoices')
      .then(({ data }) => setData(data))
      .catch(() => setData({ invoices: [], billing_status: 'ok', config: {} }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-cnib-blue" />
      </div>
    );
  }

  const overdue = data.billing_status === 'overdue';

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="member-dues-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Membresía</p>
        <h1 className="mt-1 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">Mis cuotas</h1>
      </div>

      <div
        className={`flex items-start gap-4 rounded-2xl p-6 ${overdue ? 'bg-red-50 border border-red-200' : 'bg-emerald-50 border border-emerald-200'}`}
        data-testid="billing-status-card"
      >
        {overdue ? (
          <AlertTriangle className="mt-0.5 h-7 w-7 shrink-0 text-red-500" />
        ) : (
          <CheckCircle2 className="mt-0.5 h-7 w-7 shrink-0 text-emerald-500" />
        )}
        <div>
          <h2 className={`font-heading text-lg font-bold ${overdue ? 'text-red-700' : 'text-emerald-700'}`}>
            {overdue ? 'Membresía en mora' : 'Membresía al día'}
          </h2>
          <p className={`mt-1 text-sm ${overdue ? 'text-red-600' : 'text-emerald-600'}`}>
            {overdue
              ? 'Tienes cuotas vencidas. Al regularizar tu pago se reactivan automáticamente tu credencial y la publicación de propiedades.'
              : 'No tienes cuotas vencidas. Tu credencial y todos los módulos están activos.'}
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center gap-2 border-b border-slate-100 px-6 py-4">
          <Wallet className="h-5 w-5 text-cnib-blue" />
          <h2 className="font-heading font-bold text-slate-900">Historial de cuotas</h2>
        </div>
        {data.invoices.length === 0 ? (
          <p className="px-6 py-10 text-center text-sm text-slate-500" data-testid="invoices-empty">
            Todavía no tienes cuotas generadas.
          </p>
        ) : (
          <table className="w-full text-left text-sm" data-testid="invoice-table">
            <thead className="border-b border-slate-100 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-6 py-3">Período</th>
                <th className="px-6 py-3">Monto</th>
                <th className="px-6 py-3">Vence</th>
                <th className="px-6 py-3">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.invoices.map((inv) => (
                <tr key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`}>
                  <td className="px-6 py-3.5 font-bold text-slate-800">{inv.period}</td>
                  <td className="px-6 py-3.5 text-slate-600">US$ {Number(inv.amount).toLocaleString('es-BO')}</td>
                  <td className="px-6 py-3.5 text-slate-600">
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-slate-400" />
                      {new Date(inv.due_date).toLocaleDateString('es-BO', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </td>
                  <td className="px-6 py-3.5">
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_BADGE[inv.status]}`}>
                      {STATUS_LABELS[inv.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex items-start gap-3 rounded-2xl border border-cnib-blue/15 bg-cnib-blue/5 p-5">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-cnib-blue" />
        <p className="text-sm leading-relaxed text-slate-600">
          Para pagar tu cuota, coordina con tesorería de la CNIB por WhatsApp o en oficina. Una vez confirmado, el pago se
          registra en la plataforma y tu estado se actualiza automáticamente. El pago con QR en línea estará disponible
          próximamente.
        </p>
      </div>
    </div>
  );
}
